﻿import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Callout,
    DetailsList,
    IColumn,
    SelectionMode,
    DetailsListLayoutMode,
    IDetailsHeaderProps,
    Stack,
    Checkbox,
    ScrollablePane,
    ScrollbarVisibility,
    IconButton,
    IIconProps,
    Text,
    TooltipHost,
    DetailsRow,
    IDetailsRowProps,
    CheckboxVisibility,
    SearchBox,
    ContextualMenu,
    IContextualMenuProps
} from '@fluentui/react';
import { toast } from "react-toastify";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import debounce from 'lodash/debounce';
import styles from './DataVisualizationCallout.module.css';
import { gselectedgroup } from "../../interfaces";
import { BiPurchaseTagAlt } from "react-icons/bi";
import { tooltipHtmlBasic } from "../detailedList/detailedMdls";
import { getLocInvItemCols, setLocInvItemCols, getLocRecoColOrder, setLocRecoColOrder } from "../../util_glob"
import { onRenderDetailsHeaderOrders } from '../detailedList/detailedMdls';
import { controlInvStyles, stylesScrl, recoFieldSelectCheckBoxStyles } from "../../styles_glob"
import { invItemsMainSaveButtonStyles, invPOTableDisplayRenames, invRecoTableStyles, tooltipInInvItemTableControlsStyles } from '../../inv_lsts';
import { FaListCheck } from "react-icons/fa6";
import { MdDragIndicator } from "react-icons/md";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa6";
import { FaCodeCompare } from "react-icons/fa6";

interface DataVisualizationCalloutProps {
    recoClean: any;
    selectedGroup: gselectedgroup;
    title?: string;
    buttonText?: string;
    columnOrder?: string[];
    overviewText?: string;

}
import smoothScrollIntoView from "smooth-scroll-into-view-if-needed";

interface ProcessedItem {
    [key: string]: any;
}

const closeIcon: IIconProps = { iconName: 'Cancel' };
const settingsIcon: IIconProps = { iconName: 'Settings' };

// Sortable/draggable row in the column settings slideout - visibility toggle + 3-state sort + drag handle
interface SortableColumnItemProps {
    column: string;
    label: string;
    isVisible: boolean;
    sortState: 'asc' | 'desc' | 'none';
    onToggleVisible: () => void;
    onCycleSort: () => void;
    isHighlighted?: boolean;        // matches the column search term
    searchTerm?: string;            // raw term for label highlighting
}

// build a case-insensitive regex from a search term, treating `*` as a wildcard
const buildColSearchPattern = (term: string): string =>
    term.trim().replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");

// render a label with the matched phrase highlighted
const highlightColLabel = (value: string, term: string): React.ReactNode => {
    if (!term || term.trim().length < 1) return value;
    let re: RegExp;
    try {
        re = new RegExp(`(${buildColSearchPattern(term)})`, "ig");
    } catch {
        return value;
    }
    const parts = value.split(re);
    return parts.map((part, i) =>
        part && i % 2 === 1
            ? <mark key={i} className={styles.colSearchHl}>{part}</mark>
            : <span key={i}>{part}</span>
    );
};

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({ column, label, isVisible, sortState, onToggleVisible, onCycleSort, isHighlighted, searchTerm }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            data-colkey={column}
            className={styles.sortableColumnItem + (isHighlighted ? " " + styles.sortableColumnHighlight : "")}
        >
            <span className={styles.dragHandle} {...attributes} {...listeners}>
                <MdDragIndicator size={16} color="#888" />
            </span>
            <Checkbox
                label={(searchTerm && searchTerm.trim().length > 0) ? undefined : label}
                onRenderLabel={(searchTerm && searchTerm.trim().length > 0)
                    ? () => <span className={styles.sortableColumnLabelText}>{highlightColLabel(label, searchTerm)}</span>
                    : undefined}
                checked={isVisible}
                onChange={onToggleVisible}
                styles={recoFieldSelectCheckBoxStyles}
                className={styles.sortableColumnCheckbox}
            />
            <IconButton
                className={styles.sortToggleButton}
                ariaLabel="Toggle sort"
                onClick={onCycleSort}
            >
                {sortState === 'asc' ? <FaSortUp size={10} color="#00c6af" />
                    : sortState === 'desc' ? <FaSortDown size={10} color="#00c6af" />
                    : <FaSort size={14} color="#bbb" />}
            </IconButton>
        </div>
    );
};

// recoClean is gridData?.['purchase_order_info']. gridData is root (invItems is in gridData[invoice_items])
// LINK app/frontend/src/components/JsonGridInvoice/InvoiceItemsTable.tsx#setRecoClean

// ANCHOR[id=DataVisualizationCallout] DataVisualizationCallout
export const DataVisualizationCallout: React.FC<DataVisualizationCalloutProps> = ({
    recoClean,
    selectedGroup,
    title = "Data Visualization",
    buttonText = "View Data",
    columnOrder = [],
    overviewText = ""
}) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [isSlideoutVisible, setIsSlideoutVisible] = useState<boolean>(false);
    const [columnSearch, setColumnSearch] = useState<string>("");   // wildcard search in the column chooser
    const columnListRef = useRef<HTMLDivElement>(null);             // scroller for the column chooser rows
    const buttonRef = useRef<HTMLDivElement>(null);
    const [ignoredColumns, setIgnoredColumns] = useState<string[]>(Array.from(new Set(getLocInvItemCols(selectedGroup.selectionId, 'reco_matches'))));
    const [availableColumns, setAvailableColumns] = useState<string[]>([]);
    const [sortedColumn, setSortedColumn] = useState<string | null>('item_number');
    const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);
    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set(getLocInvItemCols(selectedGroup.selectionId, 'reco_matches')));
    // persisted user-defined column order (keyed by prepend + selectionId)
    const [userColumnOrder, setUserColumnOrder] = useState<string[]>(getLocRecoColOrder(selectedGroup.selectionId));
    // wildcard search: raw input text and the debounced applied term
    const [searchText, setSearchText] = useState<string>("");
    const [appliedSearch, setAppliedSearch] = useState<string>("");
    // right-click contextual menu for header cells
    const [contextMenuProps, setContextMenuProps] = useState<IContextualMenuProps | undefined>(undefined);
    const onDismiss = () => {
        setIsVisible(false);
        setIsSlideoutVisible(false);
    };
    const onShow = () => setIsVisible(true);
    const toggleSlideout = () => setIsSlideoutVisible(!isSlideoutVisible);

    // flatten objects and detect all possible columns
    const processedData = useMemo(() => {
        // LINK app/backend/kgnf_inv_post.py#purchase_order_info
        if (!recoClean) return { items: [], columns: [] };
        import.meta.env.DEV && console.log("‹recoClean›", recoClean);

        let matchedOrderItems: any = recoClean?.['matched_order_items'] || []

        if (!matchedOrderItems || !Array.isArray(matchedOrderItems) || matchedOrderItems.length === 0) return { items: [], columns: [] };

        import.meta.env.DEV && console.log("‹recoCleanrecoClean›", matchedOrderItems);

        const allColumns = new Set<string>();

        // flatten nested objects: obj['ai_recon']['purchase_order_id'] ⇒ obj['ai_recon.purchase_order_id']
        const flattenObject = (obj: any, prefix: string = ''): any => {
            const flattened: any = {};

            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const newKey = prefix ? `${prefix}.${key}` : key;

                    if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                        // recursively flatten nested objects
                        const nested = flattenObject(obj[key], newKey);
                        Object.assign(flattened, nested);
                    } else {
                        // handle primitive values and arrays
                        flattened[newKey] = Array.isArray(obj[key])
                            ? JSON.stringify(obj[key])
                            : obj[key];
                        allColumns.add(newKey);
                    }
                }
            }
            return flattened;
        };

        // process all items and collect columns
        const processedItems: ProcessedItem[] = matchedOrderItems.map((item, index) => {
            const flattened = flattenObject(item);
            return {
                key: index.toString(),
                ...flattened
            };
        });

        // convert columns set to array and sort
        const columnsArray = Array.from(allColumns);

        import.meta.env.DEV && console.log("‹processedItems›", processedItems);

        return { items: processedItems, columns: columnsArray };
    }, [recoClean]);

    // apply column ordering - persisted user order wins, then columnOrder prop, then unmatched last
    const orderedColumns = useMemo(() => {
        // prefer the persisted user-defined order if it still matches current columns
        const baseOrder = (userColumnOrder && userColumnOrder.length > 0) ? userColumnOrder : columnOrder;

        if (!baseOrder || baseOrder.length === 0) return processedData.columns.sort();

        const orderedCols: string[] = [];
        const unmatchedCols: string[] = [];

        // first add columns in the specified order
        baseOrder.forEach(orderCol => {
            if (processedData.columns.includes(orderCol)) orderedCols.push(orderCol);
        });

        // then add unmatched columns at the end
        processedData.columns.forEach(col => {
            if (!baseOrder.includes(col)) unmatchedCols.push(col);
        });

        return [...orderedCols, ...unmatchedCols.sort()];
    }, [processedData.columns, columnOrder, userColumnOrder]);

    // Update available columns when data changes
    useEffect(() => {
        setAvailableColumns(orderedColumns);
    }, [orderedColumns]);

    // Handle column sorting
    const onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
        if (!column) return;

        // Toggle sort direction if same column, otherwise default to ascending
        const newIsSortedDescending = column.key === sortedColumn ? !isSortedDescending : false;

        setSortedColumn(column.key);
        setIsSortedDescending(newIsSortedDescending);
    };

    // parse a value that may be a number or numeric string (handles currency symbols,
    // thousands separators, parentheses negatives, trailing %). Returns null if not numeric.
    const parseNumericValue = (value: any): number | null => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number') return isNaN(value) ? null : value;
        let s = String(value).trim();
        if (s.length === 0) return null;
        // detect accounting-style negatives e.g. (1,234.50)
        let negative = false;
        if (/^\(.*\)$/.test(s)) {
            negative = true;
            s = s.slice(1, -1);
        }
        // strip currency symbols, spaces, thousands separators, trailing percent
        s = s.replace(/[^0-9.,\-]/g, '');
        // if both comma and dot present, assume comma is a thousands separator
        if (s.includes(',') && s.includes('.')) {
            s = s.replace(/,/g, '');
        } else if (s.includes(',') && !s.includes('.')) {
            // lone comma: treat as decimal separator
            s = s.replace(/,/g, '.');
        }
        if (s.length === 0 || s === '-' || s === '.') return null;
        const n = Number(s);
        if (isNaN(n)) return null;
        return negative ? -n : n;
    };

    // sum all values in a column, ignoring non-numeric entries, copy to clipboard and toast
    const calculateColumnTotal = (columnName: string): void => {
        const label = invPOTableDisplayRenames.get(columnName)?.name || columnName;
        let total = 0;
        let counted = 0;
        sortedItems.forEach(item => {
            const parsed = parseNumericValue(item[columnName]);
            if (parsed !== null) {
                total += parsed;
                counted += 1;
            }
        });

        if (counted === 0) {
            toast.info(`No numeric values found in "${label}"`, { position: "top-center", autoClose: 2500 });
            return;
        }

        // keep a clean numeric string (trim trailing zeros from float math)
        const totalStr = String(Number(total.toFixed(10)));
        navigator.clipboard?.writeText(totalStr);
        toast.success(`Total of "${label}": ${totalStr} (copied, ${counted} value${counted > 1 ? 's' : ''})`, { position: "top-center", autoClose: 3000 });
    };

    // right-click on a header cell → show contextual menu callout
    const onColumnHeaderContextMenu = (column?: IColumn, ev?: React.MouseEvent<HTMLElement>): void => {
        if (!column || !ev) return;
        ev.preventDefault();
        const columnKey = column.fieldName || column.key;
        setContextMenuProps({
            items: [
                {
                    key: 'calcTotal',
                    text: 'Calculate Total',
                    iconProps: { iconName: 'Sigma' },
                    onClick: () => {
                        calculateColumnTotal(columnKey);
                        setContextMenuProps(undefined);
                    }
                }
            ],
            target: ev.currentTarget,
            onDismiss: () => setContextMenuProps(undefined),
            calloutProps: { className: styles.headerContextMenu }
        });
    };

    // 3-state sort toggle for a field: ascending → descending → unsorted
    const cycleSort = (columnName: string): void => {
        if (sortedColumn !== columnName) {
            setSortedColumn(columnName);
            setIsSortedDescending(false);
        } else if (!isSortedDescending) {
            setIsSortedDescending(true);
        } else {
            setSortedColumn(null);
            setIsSortedDescending(false);
        }
    };

    // dnd-kit sensors for reordering columns in the slideout
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // persist new column order on drag end
    const onColumnDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = availableColumns.indexOf(String(active.id));
        const newIndex = availableColumns.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;

        const reordered = arrayMove(availableColumns, oldIndex, newIndex);
        setAvailableColumns(reordered);
        setUserColumnOrder(reordered);
        setLocRecoColOrder(selectedGroup.selectionId, reordered);
    };

    // wildcard-match the column chooser search against both the raw key and the display label
    const { columnMatchSet, firstMatchColumn } = useMemo(() => {
        const term = columnSearch.trim();
        if (term.length < 1) return { columnMatchSet: new Set<string>(), firstMatchColumn: null as string | null };
        let re: RegExp;
        try {
            re = new RegExp(buildColSearchPattern(term), "i");
        } catch {
            return { columnMatchSet: new Set<string>(), firstMatchColumn: null as string | null };
        }
        const set = new Set<string>();
        let first: string | null = null;
        for (const col of availableColumns) {
            const label = invPOTableDisplayRenames.get(col)?.name || col;
            if (re.test(col) || re.test(label)) {
                set.add(col);
                if (first === null) first = col;
            }
        }
        return { columnMatchSet: set, firstMatchColumn: first };
    }, [columnSearch, availableColumns]);

    useEffect(() => {
        if (!isSlideoutVisible) setColumnSearch("");
    }, [isSlideoutVisible]);

    // scroll the first matching column into view (centered) when the search term changes
    useEffect(() => {
        if (!firstMatchColumn || !columnListRef.current) return;
        const esc = window.CSS?.escape ? window.CSS.escape(firstMatchColumn) : firstMatchColumn.replace(/"/g, '\\"');
        const row = columnListRef.current.querySelector<HTMLElement>(`[data-colkey="${esc}"]`);
        if (row) {
            const id = window.setTimeout(() => smoothScrollIntoView(row, { behavior: 'smooth', block: 'center' }), 50);

            return () => window.clearTimeout(id);
        }
    }, [firstMatchColumn, columnSearch]);

    // debounced apply of the wildcard search term (only active when > 3 chars); runs off the render path
    const debouncedApplySearch = useMemo(
        () => debounce((val: string) => {
            setAppliedSearch(val.trim().length > 3 ? val.trim() : "");
        }, 300),
        []
    );

    useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

    const onSearchChange = useCallback((_?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
        const val = newValue ?? "";
        setSearchText(val);
        debouncedApplySearch(val);
    }, [debouncedApplySearch]);

    const clearSearch = useCallback(() => {
        debouncedApplySearch.cancel();
        setSearchText("");
        setAppliedSearch("");
    }, [debouncedApplySearch]);

    // Generate DetailsList columns dynamically
    const detailsListColumns = useMemo((): IColumn[] => {
        const visibleColumns = availableColumns.filter(col => !ignoredColumns.includes(col));

        return visibleColumns.map((columnName) => ({
            key: columnName,
            name: invPOTableDisplayRenames.get(columnName)?.name || columnName, // use last part after dot for display
            fieldName: columnName,
            minWidth: invPOTableDisplayRenames.get(columnName)?.minWidth || 150,
            currentWidth: invPOTableDisplayRenames.get(columnName)?.width || 250,
            maxWidth: invPOTableDisplayRenames.get(columnName)?.maxWidth || undefined,
            isResizable: true,
            isSorted: sortedColumn === columnName,
            isSortedDescending: sortedColumn === columnName ? isSortedDescending : false,
            onColumnClick: onColumnClick,
            onRender: (item: ProcessedItem) => {
                const value = item[columnName];
                if (value === null || value === undefined) return <div className={styles.cellText}><div className={styles.cellInnerText}>-</div></div>;

                const stringValue = String(value);
                return (
                    <div title={stringValue} className={columnName == 'ai_recon.difference_in_quantity' && stringValue != "0" ? styles.cellTextWarn : styles.cellText}>
                        {
                            columnName == 'ai_recon.reasoning_for_match' ? (
                                <TooltipHost content={<div className={styles.cellTooltipText} dangerouslySetInnerHTML={{ __html: stringValue }}/>} calloutProps={{ gapSpace: 0 }} styles={tooltipInInvItemTableControlsStyles}>
                                    <div className={styles.cellInnerText} dangerouslySetInnerHTML={{ __html: stringValue }}/>
                                </TooltipHost>
                            ) : (
                                <div className={styles.cellInnerText}>
                                    {stringValue}
                                </div>
                            )
                        }

                    </div>
                );
            }
        }));
    }, [availableColumns, ignoredColumns, sortedColumn, isSortedDescending]);

    const toggleColumnIgnore = (columnName: string) => {
        if (ignoredColumns.includes(columnName)) {
            let newIgnored = ignoredColumns.filter(col => col !== columnName);
            setIgnoredColumns(newIgnored);
            setLocInvItemCols(selectedGroup.selectionId, new Set(newIgnored), 'reco_matches');
        } else {
            setIgnoredColumns([...ignoredColumns, columnName]);
            setLocInvItemCols(selectedGroup.selectionId, new Set([...ignoredColumns, columnName]), 'reco_matches');
        }
    };

    // Wildcard filter across all fields (case-insensitive). Empty term = no filtering.
    // 🔍 filtered items ----
    const filteredItems = useMemo(() => {
        if (!appliedSearch) return processedData.items;
        const needle = appliedSearch.toLowerCase();
        return processedData.items.filter(item =>
            Object.keys(item).some(k => {
                if (k === 'key') return false;
                const v = item[k];
                return v !== null && v !== undefined && String(v).toLowerCase().includes(needle);
            })
        );
    }, [processedData.items, appliedSearch]);

    // Sort items based on selected column
    const sortedItems = useMemo(() => {
        if (!sortedColumn) return filteredItems;

        const sorted = [...filteredItems].sort((a, b) => {
            const aVal = a[sortedColumn];
            const bVal = b[sortedColumn];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            // Try numeric comparison first
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }

            // Fall back to string comparison
            return String(aVal).localeCompare(String(bVal));
        });

        return isSortedDescending ? sorted.reverse() : sorted;
    }, [filteredItems, sortedColumn, isSortedDescending]);

    const getHighlightRowClass = (prps: IDetailsRowProps | undefined): string => {
        return  (prps?.item?.['ai_recon.invoice_item_number'] ?? 0) == 0 ? styles.invTableTotalCellMiss : styles.invTableTotalCellGood;
    }
    return (
        <div>
            <div ref={buttonRef} className={styles.triggerButton}>
            <TooltipHost content={tooltipHtmlBasic("Show connected data", "Show matching PO/Delivery Notes/Products/Nomenclatures")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInInvItemTableControlsStyles}>
                <IconButton
                    onClick={onShow}
                    styles={invItemsMainSaveButtonStyles}
                    disabled={processedData.items.length === 0}
                >
                    <FaCodeCompare size={20} color="#00c6af" />
                </IconButton>
                </TooltipHost>
            </div>
            {/* Connected POs Table Callout */}
            {isVisible && (
                <Callout
                    target={buttonRef}
                    onDismiss={onDismiss}
                    setInitialFocus
                    calloutMinWidth={window.innerWidth * 0.8}
                    className={styles.orderTableCallout}
                    isBeakVisible={true}
                >
                    <div className={styles.container}>
                        {/* Header */}
                        <Stack horizontal className={styles.header} >
                            <div  className={styles.title}>
                                {'Matched Orders'}
                            </div>
                            <div className={styles.orderMatchOverview}>
                                <div className={styles.overviewHtml} dangerouslySetInnerHTML={{__html: overviewText}}></div>
                            </div>



                        </Stack>
                        <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 8 }} className={styles.headerControls}>

                            <IconButton iconProps={closeIcon} ariaLabel="Close" onClick={onDismiss}/>
                        </Stack>
                        {/* Search bar - right aligned above the table */}
                        <div className={styles.searchRow}>
                            <div className={styles.searchWrap}>
                                <SearchBox
                                    placeholder="Search all fields..."
                                    value={searchText}
                                    onChange={onSearchChange}
                                    onClear={clearSearch}
                                    className={styles.searchBox}
                                    underlined
                                />
                                {appliedSearch && (
                                    <div className={styles.searchTag}>
                                        <span className={styles.searchTagText} title={appliedSearch}>
                                            {appliedSearch.slice(0, 12)}{appliedSearch.length > 12 ? '…' : ''}
                                        </span>
                                        <IconButton
                                            iconProps={closeIcon}
                                            ariaLabel="Clear search"
                                            onClick={clearSearch}
                                            className={styles.searchTagClose}
                                        />
                                    </div>
                                )}

                            </div>
                            <IconButton
                                    iconProps={settingsIcon}
                                    ariaLabel="Column Settings"
                                    onClick={toggleSlideout}
                                    className={styles.columnToggleButton}
                                />
                        </div>

                        {/* Data Table */}
                        <div className={styles.dataTable}>
                            {processedData.items.length > 0 ? (
                                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={stylesScrl} >
                                    <DetailsList
                                        items={sortedItems}
                                        className={styles.orderDetList}
                                        styles={controlInvStyles.styles}
                                        columns={detailsListColumns}
                                        onColumnHeaderContextMenu={onColumnHeaderContextMenu}
                                        onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeaderOrders(props!)}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        isHeaderVisible={true}
                                        compact={true}
                                        onShouldVirtualize={() => processedData.items.length > 50}
                                        onRenderRow={(prps: IDetailsRowProps | undefined): JSX.Element => (
                                            <div className={styles.shtx}>
                                                <DetailsRow
                                                    {...(prps as IDetailsRowProps)}
                                                    className={getHighlightRowClass(prps)}
                                                    compact={true}
                                                    checkboxVisibility={CheckboxVisibility.hidden}
                                                    selectionMode={2}
                                                    styles={invRecoTableStyles}
                                                />
                                            </div>
                                        )}
                                    />
                                </ScrollablePane>
                            ) : (
                                <div className={styles.noDataContainer}>
                                    <Text variant="large" className={styles.noDataText}>
                                        No data available
                                    </Text>
                                </div>
                            )}
                        </div>

                            {/* Data Summary */}
                            <Stack horizontal tokens={{ childrenGap: 10 }} className={styles.dataSummary}>
                                <Text variant="medium" className={styles.dataSummaryText}>
                                    <strong>Total Records:</strong> {appliedSearch ? `${sortedItems.length} / ${processedData.items.length}` : processedData.items.length}
                                </Text>
                                <Text variant="medium" className={styles.dataSummaryText}>
                                    <strong>Total Columns:</strong> {processedData.columns.length}
                                </Text>
                                <Text variant="medium" className={styles.dataSummaryText}>
                                    <strong>Visible Columns:</strong> {detailsListColumns.length}
                                </Text>
                            </Stack>
                        {/* Footer */}


                        {/* Column Settings Slideout */}
                        {isSlideoutVisible && (
                            <>
                                <div className={styles.overlay} onClick={() => setIsSlideoutVisible(false)} />
                                <div className={styles.columnSlideout}>
                                    <div className={styles.slideoutHeader}>
                                        <Text variant="mediumPlus" className={styles.slideoutTitle}>
                                            Columns (from Inv. & PO)
                                        </Text>
                                        <IconButton
                                            iconProps={closeIcon}
                                            ariaLabel="Close"
                                            onClick={() => setIsSlideoutVisible(false)}
                                        />
                                    </div>
                                    <div className={styles.columnSearchWrap}>
                                        <SearchBox
                                            placeholder="Search Column Names"
                                            value={columnSearch}
                                            onChange={(_, v) => setColumnSearch(v || "")}
                                            onClear={() => setColumnSearch("")}

                                            underlined
                                        />
                                    </div>
                                    <div className={styles.columnCheckboxes} ref={columnListRef}>
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                                            onDragEnd={onColumnDragEnd}
                                        >
                                            <SortableContext items={availableColumns} strategy={verticalListSortingStrategy}>
                                                {availableColumns.map((column) => {
                                                    const label = invPOTableDisplayRenames.get(column)?.name || column;
                                                    const isMatch = columnMatchSet.has(column);
                                                    return (
                                                        <SortableColumnItem
                                                            key={column}
                                                            column={column}
                                                            label={label}
                                                            isVisible={!ignoredColumns.includes(column)}
                                                            sortState={sortedColumn === column ? (isSortedDescending ? 'desc' : 'asc') : 'none'}
                                                            onToggleVisible={() => toggleColumnIgnore(column)}
                                                            onCycleSort={() => cycleSort(column)}
                                                            isHighlighted={isMatch}
                                                            searchTerm={columnSearch}
                                                        />
                                                    );
                                                })}
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {contextMenuProps && <ContextualMenu {...contextMenuProps} />}
                </Callout>
            )}
        </div>
    );
};

export default DataVisualizationCallout;